// modules/freshcards/src/core/overlays/uiStore.ts
//
// 2026-09-18 (PROMPT F 20 Phase 1+2) — UI ephemeral state for the overlay
// picker. Pure module (no React) with a subscribable API. Holds transient
// state (previewing, applying, last error) that doesn't belong on the
// OverlayRegistry or FacePreference.
//
// Boundaries:
//   - OverlayRegistry: canonical overlay data + lifecycle events.
//   - FacePreference storage: persistent scope-keyed preferences.
//   - uiStore: ephemeral UI state only. Resets on app reload.

import type { AppOverlay } from '../../types/models/AppOverlay';
import type { FaceScope } from './facePreference';

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

export interface PreviewState {
  overlayId: string;
  scope: FaceScope;
  /** Source preview — iframe vs static thumbnail. */
  kind: 'iframe' | 'thumbnail';
  /** Started timestamp. */
  startedAt: number;
}

export interface OverlayApplyError {
  overlayId: string;
  scope: FaceScope;
  reason: string;
  timestamp: number;
}

export interface UIStoreState {
  previewing: PreviewState | null;
  applying: { overlayId: string; scope: FaceScope } | null;
  lastError: OverlayApplyError | null;
  /** Diff between current applied overlay and a candidate (for "show diff BEFORE applying"). */
  pendingDiff: {
    candidate: AppOverlay;
    current: AppOverlay | null;
    changedFields: string[];
  } | null;
}

// ---------------------------------------------------------------------------
// Singleton + subscribe
// ---------------------------------------------------------------------------

type Listener = (state: UIStoreState) => void;

const initial: UIStoreState = {
  previewing: null,
  applying: null,
  lastError: null,
  pendingDiff: null,
};

let _state: UIStoreState = initial;
const _listeners: Set<Listener> = new Set();

function notify(): void {
  for (const l of _listeners) {
    try {
      l(_state);
    } catch (e) {
      console.error('[uiStore] listener error', e);
    }
  }
}

export function getUIState(): UIStoreState {
  return _state;
}

export function subscribeUI(listener: Listener): () => void {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export function setPreview(p: PreviewState | null): void {
  _state = { ..._state, previewing: p };
  notify();
}

export function clearPreview(): void {
  if (_state.previewing === null) return;
  _state = { ..._state, previewing: null };
  notify();
}

export function setApplying(a: { overlayId: string; scope: FaceScope } | null): void {
  _state = { ..._state, applying: a };
  notify();
}

export function setPendingDiff(diff: UIStoreState['pendingDiff']): void {
  _state = { ..._state, pendingDiff: diff };
  notify();
}

export function clearPendingDiff(): void {
  if (_state.pendingDiff === null) return;
  _state = { ..._state, pendingDiff: null };
  notify();
}

export function setError(e: OverlayApplyError | null): void {
  _state = { ..._state, lastError: e };
  notify();
}

export function clearError(): void {
  if (_state.lastError === null) return;
  _state = { ..._state, lastError: null };
  notify();
}

/** Reset all UI state — used when leaving the picker. */
export function resetUIStore(): void {
  _state = initial;
  notify();
}

// ---------------------------------------------------------------------------
// React hook helper (lightweight; no React import in this file)
// ---------------------------------------------------------------------------

/**
 * Minimal React hook shape. Components should re-implement via useState +
 * subscribeUI if React is available. Kept here as a documented pattern, not
 * imported (avoids forcing React in this pure module).
 */
export type UseUIStore<S> = () => S;
